import { Spin as AntSpin } from 'antd';
import React, { memo } from 'react';

import { AntLoading } from '@fmbt/ui/icons';
import FarmbotLogoHorizontal from 'components/atoms/FarmbotLogoHorizontal';

type LoadingSpinnerProps = {
  /** Pixel font size for the indicator; when omitted, antd `size="large"` is used (~32px). */
  size?: number;
  color?: React.CSSProperties['color'];
};

type LoadingLogoProps = {
  subtitle?: string;
};

function LoadingSpinner({
  size,
  color
}: LoadingSpinnerProps): JSX.Element {
  const indicatorStyle: React.CSSProperties = {
    ...(color !== undefined ? { color } : {}),
    ...(size !== undefined ? { fontSize: size } : {})
  };
  const indicator = <AntLoading style={indicatorStyle} />;
  return (
    <AntSpin
      indicator={indicator}
      size={size === undefined ? 'large' : undefined}
    />
  );
}

function LoadingLogo({ subtitle }: LoadingLogoProps): JSX.Element {
  return (
    <div style={{
      // Center the content on the screen
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      // Spacing between the logo and the subtitle
      ...(subtitle ? {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        textAlign: 'center' // Ensure text wraps nicely
      } : {})
    }}>
      <FarmbotLogoHorizontal />
      {subtitle && <div className="body-muted-copy">{subtitle}</div>}
    </div>
  );
}

/** Flex-centred wrapper, full width and full height but can be overridden; always renders default LoadingSpinner. */
function LoadingSpinnerDiv({ height, width }: {
  height?: React.CSSProperties['height'];
  width?: React.CSSProperties['width'];
}): JSX.Element {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: width ?? '100%',
      height: height ?? '100%'
    }}>
      <LoadingSpinner />
    </div>
  );
}

const LoadingLogoMemo = memo(LoadingLogo);
const LoadingSpinnerDivMemo = memo(LoadingSpinnerDiv);

export default memo(LoadingSpinner);
export { LoadingLogoMemo as LoadingLogo, LoadingSpinnerDivMemo as LoadingSpinnerDiv };
export type { LoadingLogoProps, LoadingSpinnerProps };
